import React, { useEffect, useState } from 'react';
import { StatusFormSetting } from '../AssetTabs';
import { closeTriggerKeyValuePair, openTriggerKeyValuePair } from '../../SiteSummary/constants';
import LoaderOverlay from '../../common/Loader/LoaderOverlay';


interface FieldState {
  [key: string]: string;
}

type SetPageFunction = React.Dispatch<React.SetStateAction<{ [key: string]: FieldState; }>>;
type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
type SetChangeFieldFunction = React.Dispatch<React.SetStateAction<{
  [formName: string]: Set<string>;
}>>
type DropdownOption = {
  text: string;
  value: number;
  status?: any;
};

interface PlungerFormProps {
  formState: { [key: string]: FieldState };
  setFormState: SetPageFunction;
  setTabDivHeight: SetTabPageFunction;
  PlungerFormData: StatusFormSetting[] | undefined;
  WellsFormData?: StatusFormSetting[] | undefined;
  changedFields: { [formName: string]: Set<string> };
  setChangedFields: SetChangeFieldFunction;
  formNames: string;
  isToggle: boolean;
  description?: string;
  isSetPointLoading?: boolean;
  setIsMode?: React.Dispatch<React.SetStateAction<string>>,
  isLatestVersion: boolean
  setHasErrors?: (hasErrors: boolean) => void;

}
interface InitialValues {
  [formName: string]: {
    [fieldName: string]: string;
  };
}

// ID mapping for specific Open Trigger options
const openTriggerIdMap: Record<string, number[]> = {
  "None": [0],
  "Off Time": [13],
  "Tubing (>)": [14],
  "Tubing - Line (>)": [57],
  "Casing (>)": [15],
  "Casing - Line (>)": [155],
  "Casing - Tubing (<)": [16],
  "Load Factor (<)": [48]
};

const afterflowCloseTriggerIdMap: Record<string, number[]> = {
  "None": [0],
  "Gas Rate (<)": [52],
  "Time": [19],
  "Casing D.I.P. (>)": [20],
  "Tubing (<) ": [238],
  "Casing (<)": [239],
  "Critical Rate Multiplier": [246]
};

const PlungerForm: React.FC<PlungerFormProps> = ({ formState, isSetPointLoading, setFormState, setTabDivHeight, changedFields, setChangedFields, formNames, isToggle, PlungerFormData, setIsMode, isLatestVersion, setHasErrors }) => {
  // Only get the tubing length (id 7) from WellsFormData
  const tubingLength = PlungerFormData?.find(f => f.id === 10003)?.value;
  const [initialValues, setInitialValues] = useState<{ [formName: string]: { [fieldName: string]: string } }>({});
  const [filteredClosedInputs, setFilteredClosedInputs] = useState<any[]>([]);
  const [filteredAfterflowInputs, setFilteredAfterFlowInputs] = useState<any[]>([]);
  const toggleOnlyIds = [229, 230, 231, 232, 233, 234];
  // Define the target IDs that must gray out when Adjustment Windows is disabled
  const affectedSetpointIds = [26, 27, 28, 29, 136, 137, 138];
  const isjwtIsPlLimitedUser =
    localStorage.getItem("jwtIsPlLimitedUser") === "true" ? true : false;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rules, setRules] = useState<any>(null);

  const PAGL_FIELDS = [
    "fastOnTriggerAdjust",
    "slowOnTriggerAdjust",
    "fastOffTriggerAdjust",
    "slowOffTriggerAdjust",
    "noArrivalOnAdjust",
    "noArrivalOffAdjust",
    "girMin",
    "girMax",
  ];

  const closedInputs = PlungerFormData?.filter(
    (item) => item.subGroup === "Closed"
  ) || [];

  const FormName = formNames
  const controlSetupValue = formState[FormName]?.mode;
  const ishighLinePresseureMode = formState["System"]?.highLinePressureMode === "0"
  const wellMethod = formState?.["Well"]?.method?.toString() ?? "";
  const isWellMethodConventional = wellMethod.trim().toLowerCase() === "0";

  useEffect(() => {

    setIsMode?.(controlSetupValue)

    setTabDivHeight(controlSetupValue === "4" ? "1900px" : "1500px");
  }, [controlSetupValue]);

  useEffect(() => {

    const initialValue = {};

    Object.keys(formState).forEach(formName => {
      initialValue[formName] = {};
      Object.keys(formState[formName]).forEach(fieldName => {
        initialValue[formName][fieldName] = formState[formName][fieldName];
      });
    });
    setInitialValues(initialValue);
    setChangedFields({})

  }, [formNames, isToggle]);

  useEffect(() => {
    const isPAGLMode = controlSetupValue === "4";

    const filteredErrors = Object.keys(errors).filter((key) => {
      if (isPAGLMode) return true;

      return !PAGL_FIELDS.includes(key);
    });


    setHasErrors?.(filteredErrors.length > 0);

  }, [errors, controlSetupValue]);

  useEffect(() => {
    const closedInputs = PlungerFormData?.filter(
      (item) => item.subGroup === "Closed"
    ) || [];

    const afterflowInputs = PlungerFormData?.filter(
      (item) => item.subGroup === "Afterflow"
    ) || [];

    const openTriggerInput = closedInputs.find(
      input => input.name.toLowerCase() === "opentrigger"
    );
    const selectedOpenTrigger = openTriggerInput?.value;
    const openTriggerOptions = openTriggerInput?.options || [];

    const closeTriggerInput = afterflowInputs.find(
      input => input.name.toLowerCase() === "closetrigger"
    );
    const selectedCloseTrigger = closeTriggerInput?.value;
    const closeTriggerOptions = closeTriggerInput?.options || [];

    const filteredClosed = selectedOpenTrigger
      ? filterInputsByTriggerMap(
        closedInputs,
        selectedOpenTrigger,
        openTriggerOptions,
        openTriggerIdMap
      )
      : closedInputs;

    const filteredAfterflow = selectedCloseTrigger
      ? filterInputsByTriggerMap(
        afterflowInputs,
        selectedCloseTrigger,
        closeTriggerOptions,
        afterflowCloseTriggerIdMap
      )
      : afterflowInputs;

    setFilteredClosedInputs(filteredClosed);
    setFilteredAfterFlowInputs(filteredAfterflow); // <-- Add this state if not already present
  }, [
    formState.Closed?.OpenTrigger,
    formState.Afterflow?.CloseTrigger,
    PlungerFormData
  ]);


  const getFormattedGroupName = (groupName: string): string => {
    const customNames: Record<string, string> = {
      Closed: "CLOSED",
      Lifting: "LIFTING",
      Afterflow: "AFTERFLOW",
      CloseAutoAdjustments: "CLOSE AUTO ADJUJSTMENT",
      AfterflowAdjustments: "ADJUSTMENT WINDOWS",
      AfterflowAutoAdjustments: "AFTERFLOW AUTO ADJUSTMENT",
    };

    return customNames[groupName] || groupName;
  };


  const filterInputsByTriggerMap = (
    allInputs: any[],
    selectedValue: string,
    triggerOptions: { value: number; text: string }[],
    triggerIdMap: Record<string, number[]>
  ) => {
    const selectedOption = triggerOptions.find(
      (opt) => opt?.value?.toString() === selectedValue
    );
    const selectedLabel = selectedOption?.text || "None";

    const allowedIds = triggerIdMap[selectedLabel] || [];
    const allMapIds = Object.values(triggerIdMap).flat();

    return allInputs.filter((input) => {
      // Always show inputs not involved in trigger map (e.g., static fields like OpenTrigger/CloseTrigger)
      const isDynamic = allMapIds.includes(input.id);

      // If "None" is selected and the input is dynamic → hide it
      if (selectedLabel === "None" && isDynamic) {
        return false;
      }

      // Otherwise apply allowed filtering for dynamic inputs
      return !isDynamic || allowedIds.includes(input.id);
    });
  };

  const buildValidationRules = (formData: StatusFormSetting[]) => {
    const rules = {
      minMax: [] as { min: string; max: string }[],
      fastSlow: [] as { fast: string; slow: string }[],
      fastSlowTime: [] as { fast: string; slow: string }[],
      afterflow: [] as { min: string; max: string }[],
    };

    const normalized = (str: string) =>
      str.toLowerCase().replace(/[^a-z]/g, "");

    const minFields: Record<string, string> = {};
    const maxFields: Record<string, string> = {};

    formData.forEach((item) => {
      const label = normalized(item.label || "");

      if (label.includes("min")) {
        const key = label.replace("min", "");
        minFields[key] = item.name;
      }

      if (label.includes("max")) {
        const key = label.replace("max", "");
        maxFields[key] = item.name;
      }
    });

    let afterflowTimeField = "";
    let maxAfterflowTimeField = "";

    formData.forEach((item) => {
      const label = normalized(item.label || "");
      const group = item.subGroup?.toLowerCase() || "";

      if (group === "afterflow") {
        if (label === "afterflowtime") {
          afterflowTimeField = item.name;
        }

        if (label === "maxafterflowtime") {
          maxAfterflowTimeField = item.name;
        }
      }
    });

    if (afterflowTimeField && maxAfterflowTimeField) {
      rules.afterflow.push({
        min: afterflowTimeField,
        max: maxAfterflowTimeField,
      });
    }

    Object.keys(minFields).forEach((key) => {
      if (maxFields[key]) {
        rules.minMax.push({
          min: minFields[key],
          max: maxFields[key],
        });
      }
    });

    const fastFields: Record<string, string> = {};
    const slowFields: Record<string, string> = {};

    formData.forEach((item) => {
      const label = normalized(item.label || "");

      const group = item.subGroup?.toLowerCase() || "";

      if (
        group.includes("afterflow auto") ||
        group.includes("close auto")
      ) return;

      if (label.includes("fast")) {
        const key = label.replace("fast", "");
        fastFields[key] = item.name;
      }

      if (label.includes("slow")) {
        const key = label.replace("slow", "");
        slowFields[key] = item.name;
      }
    });

    Object.keys(fastFields).forEach((key) => {
      if (slowFields[key]) {
        const isTime = key.includes("time");

        if (isTime) {
          rules.fastSlowTime.push({
            fast: fastFields[key],
            slow: slowFields[key],
          });
        } else {
          rules.fastSlow.push({
            fast: fastFields[key],
            slow: slowFields[key],
          });
        }
      }
    });

    return rules;
  };

  const GROUP_TOGGLE_MAP: Record<string, string> = {
    "close auto adjustments": "CloseAutoAdjustToggle",
    "afterflow auto adjustments": "AfterflowAutoAdjustToggle",
    "adjustment windows": "AdjustmentWindowToggle",
    "pagl adjustment": "PAGLAdjustToggle",
  };

  const isValidationAllowed = (fieldName: string, form: FieldState) => {
    const field = PlungerFormData?.find(f => f.name === fieldName);
    const group = field?.subGroup?.toLowerCase();

    if (!group) return true;

    const toggleField = GROUP_TOGGLE_MAP[group];

    if (!toggleField) return true;

    const toggleValue = form[toggleField];

    return toggleValue !== "0";
  };

  const validateDynamic = (form: FieldState, rules: any, visibleAfterflowInputs?: any[]) => {
    // if (form["AdjustmentWindowToggle"] === "0") {
    //   return {};
    // }
    const errors: Record<string, string> = {};
    const getNum = (k: string) => Number(form[k] ?? 0);

    rules.minMax.forEach(({ min, max }) => {
      if (!isValidationAllowed(min, form)) return;

      const minVal = getNum(min);
      const maxVal = getNum(max);

      if (minVal === 0 || maxVal === 0) return;

      if (minVal > maxVal) {
        errors[min] = "Min cannot exceed Max";
        errors[max] = "Max must be ≥ Min";
      }
    });

    rules.fastSlow.forEach(({ fast, slow }) => {
      if (!isValidationAllowed(fast, form)) return;

      const fastField = PlungerFormData?.find(f => f.name === fast);
      const group = fastField?.subGroup?.toLowerCase();

      if (group === "pagl adjustment") return;

      const fastVal = getNum(fast);
      const slowVal = getNum(slow);

      if (fastVal === 0 || slowVal === 0) return;

      if (slowVal > fastVal) {
        errors[slow] = "Slow cannot be more aggressive than Fast";
        errors[fast] = "Fast must be ≥ Slow";
      }
    });

    rules.fastSlowTime.forEach(({ fast, slow }) => {
      if (!isValidationAllowed(fast, form)) return;

      const fastVal = getNum(fast);
      const slowVal = getNum(slow);

      if (fastVal === 0 || slowVal === 0) return;

      if (fastVal > slowVal) {
        errors[fast] = "Fast time cannot exceed Slow time";
        errors[slow] = "Slow time must be ≥ Fast time";
      }
    });

    // Danger Fast Velocity must never be lower than either Fast Velocity or Slow Velocity
    const dangerFastVelocityField = "dangerFastVelocityLimit";
    const fastVelocityField = "fastArrivalLimit";

    if (isValidationAllowed(dangerFastVelocityField, form)) {
      const dangerVal = getNum(dangerFastVelocityField);
      const fastVal = getNum(fastVelocityField);

      if (dangerVal !== 0 && fastVal !== 0 && dangerVal < fastVal) {
        errors[dangerFastVelocityField] = "Danger Fast Velocity must be ≥ Fast Velocity";
        errors[fastVelocityField] = "Fast Velocity must be ≤ Danger Fast Velocity";
      }
    }
    const dangerFastTimeField = "dangerFastTime";
    const fastTimeField = "fasttime";

    if (isValidationAllowed(dangerFastTimeField, form)) {
      const dangerTimeVal = getNum(dangerFastTimeField);
      const fastTimeVal = getNum(fastTimeField);

      if (dangerTimeVal !== 0 && fastTimeVal !== 0 && dangerTimeVal > fastTimeVal) {
        errors[dangerFastTimeField] = "Danger Fast Time must be ≤ Fast Time";
        errors[fastTimeField] = "Fast Time must be ≥ Danger Fast Time";
      }
    }

    rules.afterflow.forEach(({ min, max }) => {
      if (!isValidationAllowed(min, form)) return;

      const minVal = getNum(min);
      const maxVal = getNum(max);

      if (visibleAfterflowInputs) {
        const isMinFieldVisible = visibleAfterflowInputs.some(f => f.name === min);
        if (!isMinFieldVisible) return;
      }

      if (minVal === 0 || maxVal === 0) return;

      if (minVal > maxVal) {
        errors[min] = "Afterflow Time cannot exceed Max Afterflow Time";
        errors[max] = "Max Afterflow Time must be ≥ Afterflow Time";
      }
    });

    if (visibleAfterflowInputs) {
      const criticalRateField = visibleAfterflowInputs.find(f => f.id === 246);
      if (criticalRateField) {
        const rawValue = form[criticalRateField.name];
        if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
          const value = Number(rawValue);
          if (!isNaN(value) && value !== 0 && (value < 80 || value > 150)) {
            errors[criticalRateField.name] = "Allowed operating range: 80% to 150%";
          }
        }
      }
    }

    return errors;
  };

  useEffect(() => {
    if (!rules) return;

    const currentForm = formState[FormName];

    if (!currentForm) return;

    const errs = validateDynamic(currentForm, rules, filteredAfterflowInputs);

    const closeTriggerLabel = getClosedTriggerLabelFromClosedInputs(formState);
    if (closeTriggerLabel === "Critical Rate Multiplier" && PlungerFormData) {
      // Skip all validation when the Afterflow Auto Adjustments toggle is off
      const isAfterflowAutoToggleOn = currentForm["AfterflowAutoAdjustToggle"] !== "0";

      if (isAfterflowAutoToggleOn) {
        const closeTriggerMaxField = PlungerFormData.find(f => f.label?.trim() === "Close Trigger Max:");
        const closeTriggerMinField = PlungerFormData.find(f => f.label?.trim() === "Close Trigger Min:");

        const rawMax = closeTriggerMaxField ? currentForm[closeTriggerMaxField.name] : undefined;
        const rawMin = closeTriggerMinField ? currentForm[closeTriggerMinField.name] : undefined;

        const maxValue = (rawMax !== undefined && rawMax !== null && rawMax !== "") ? Number(rawMax) : NaN;
        const minValue = (rawMin !== undefined && rawMin !== null && rawMin !== "") ? Number(rawMin) : NaN;

        // Validate Max: if non-zero, must be within [80, 150]
        if (closeTriggerMaxField && !isNaN(maxValue) && maxValue !== 0) {
          if (maxValue < 80) errs[closeTriggerMaxField.name] = "Value must be ≥ 80%";
          else if (maxValue > 150) errs[closeTriggerMaxField.name] = "Value must be ≤ 150%";
        }

        // Validate Min: if non-zero, must be within [80, 150]
        if (closeTriggerMinField && !isNaN(minValue) && minValue !== 0) {
          if (minValue < 80) errs[closeTriggerMinField.name] = "Value must be ≥ 80%";
          else if (minValue > 150) errs[closeTriggerMinField.name] = "Value must be ≤ 150%";
        }

        // Cross-field: when both are non-zero and individually in range, Min must not exceed Max
        if (
          closeTriggerMaxField && closeTriggerMinField &&
          !isNaN(maxValue) && !isNaN(minValue) &&
          maxValue !== 0 && minValue !== 0 &&
          !errs[closeTriggerMaxField.name] && !errs[closeTriggerMinField.name]
        ) {
          if (minValue > maxValue) {
            errs[closeTriggerMaxField.name] = "Max must be ≥ Min";
            errs[closeTriggerMinField.name] = "Min must not exceed Max";
          }
        }
      }
    }

    setErrors(errs);

  }, [rules, formState, FormName, filteredAfterflowInputs, PlungerFormData]);

  useEffect(() => {
    if (PlungerFormData) {
      const r = buildValidationRules(PlungerFormData);
      console.log("RULES BUILT:", r); // 🔥 CHECK THIS
      setRules(r);
    }
  }, [PlungerFormData]);


  const isFieldGrayedOut = (fieldName: string): boolean => {
    const openTrigger = formState[FormName]?.openTrigger;
    const closeTrigger = formState[FormName]?.closeTrigger;

    const getOpenTriggerValues = (): number[] => {
      return openTriggerKeyValuePair.map(item => item.value);
    };

    const getCloseTriggerValues = (): number[] => {
      return closeTriggerKeyValuePair.map(item => item.value);
    };

    const normalize = (val: string) =>
      val?.toLowerCase().replace(/[:]/g, "").replace(/\s+/g, "").trim();

    const currentField = PlungerFormData?.find(field => field.name === fieldName);
    const currentLabelId = currentField?.id ?? 0;

    // Get Open Trigger Label
    const openTriggerField = PlungerFormData?.find(field => field.name === "openTrigger");
    const openTriggerOptions = openTriggerField?.options as DropdownOption[] | undefined;

    const normalizedOpenTriggerLabel =
      openTriggerOptions?.find(opt => opt.value.toString() === openTrigger)?.value.toString() ?? ""

    const openTriggerId = openTriggerKeyValuePair.find(
      item => normalize(item.key) === normalizedOpenTriggerLabel
    )?.value;

    // Get Close Trigger Label
    const closeTriggerField = PlungerFormData?.find(field => field.name === "closeTrigger");
    const closeTriggerOptions = closeTriggerField?.options as DropdownOption[] | undefined;

    const normalizedCloseTriggerLabel =
      closeTriggerOptions?.find(opt => opt.value.toString() === closeTrigger)?.value.toString() ?? ""

    const closeTriggerIds = closeTriggerKeyValuePair
      .filter(item => normalize(item.key) === normalizedCloseTriggerLabel)
      .map(item => item.value);

    const openTriggerRelatedLabels = getOpenTriggerValues();
    const closeTriggerRelatedLabels = getCloseTriggerValues();

    if (openTriggerRelatedLabels.includes(currentLabelId)) {
      return currentLabelId !== openTriggerId;
    }

    if (closeTriggerRelatedLabels.includes(currentLabelId)) {
      return !closeTriggerIds.includes(currentLabelId); // ← allow multiple "time" values
    }

    return false;
  };

  function getOpenTriggerLabelFromClosedInputs(formState: any): string {
    // Step 1: Get all Closed inputs
    const closedInputs = PlungerFormData?.filter(
      (item) => item.subGroup === "Closed"
    ) || [];

    const openTrigger = closedInputs.find(
      input => input.name.toLowerCase() === "opentrigger"
    );

    // Step 2: Find the input for 'openTrigger'
    const openTriggerInput = formState?.Plunger?.openTrigger;

    // Step 3: Get selected value and options list
    const selectedOpenTriggerValue = openTriggerInput;
    const openTriggerOptions = openTrigger?.options as any || [];

    // Step 4: Match value with label
    const matchedOption = openTriggerOptions.find(
      option => option?.value == selectedOpenTriggerValue
    );

    return matchedOption?.text || "None";
  }

  function getClosedTriggerLabelFromClosedInputs(formState: any): string {
    // Step 1: Get all Closed inputs
    const closedInputs = PlungerFormData?.filter(
      (item) => item.subGroup === "Afterflow"
    ) || [];

    // Step 2: Find the input for 'openTrigger'
    const closeTrigger = closedInputs.find(
      input => input.name.toLowerCase() === "closetrigger"
    );

    const closeTriggerInput = formState?.Plunger?.closeTrigger;

    // Step 3: Get selected value and options list
    const selectedOpenTriggerValue = closeTriggerInput;
    const closeTriggerOptions = closeTrigger?.options as any || [];
    // Step 4: Match value with label
    const matchedOption = closeTriggerOptions.find(
      option => option?.value == selectedOpenTriggerValue
    );

    return matchedOption?.text || "None";
  }


  // Handle input change for a specific field in a specific form
  const handleInputChange = (formName: string, fieldName: string, value: string) => {

    setFormState((prevFormState) => ({
      ...prevFormState,
      [formName]: {
        ...prevFormState[formName],
        [fieldName]: value,
      },
    }));

    setChangedFields((prevChangedFields) => ({
      ...prevChangedFields,
      [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
    }));
  };

  // Function to identify change in field value
  const handleFieldChange = (formName: string, fieldName: string, newValue: string) => {
    const initialValue = initialValues[formName]?.[fieldName];

    // Update changedFields tracking logic
    if (initialValue !== newValue) {
      setChangedFields((prev) => ({
        ...prev,
        [formName]: new Set([...(prev[formName] || []), fieldName]),
      }));
    } else {
      setChangedFields((prev) => {
        const updated = new Set(prev[formName] || []);
        updated.delete(fieldName);
        return { ...prev, [formName]: updated };
      });
    }

    // Special case: handle openTrigger value change
    if (fieldName === "openTrigger") {
      const closedInputs = PlungerFormData?.filter(
        (item) => item.subGroup === "Closed"
      ) || [];
      const selectedOpenTrigger = newValue

      const openTriggerInput = closedInputs.find(
        input => input.name.toLowerCase() === "opentrigger")

      const openTriggerOptions = openTriggerInput?.options || [];

      const filteredClosed = selectedOpenTrigger
        ? filterInputsByTriggerMap(
          closedInputs,
          selectedOpenTrigger,
          openTriggerOptions,
          openTriggerIdMap
        )
        : closedInputs;

      setFilteredClosedInputs(filteredClosed);
    }

    if (fieldName === "closeTrigger") {
      const afterFlowInputs = PlungerFormData?.filter(
        (item) => item.subGroup === "Afterflow"
      ) || [];
      const selectedClosedTrigger = newValue

      const closedTriggerInput = afterFlowInputs.find(
        input => input.name.toLowerCase() === "closetrigger")

      const closedTriggerOptions = closedTriggerInput?.options || [];

      const filteredClosedValue = selectedClosedTrigger
        ? filterInputsByTriggerMap(
          afterFlowInputs,
          selectedClosedTrigger,
          closedTriggerOptions,
          afterflowCloseTriggerIdMap
        )
        : closedInputs;

      setFilteredAfterFlowInputs(filteredClosedValue);
    }

    // Generic bidirectional calculation for Arrival Limit <-> Time fields
    const bidirectionalCalcMap = [
      {
        velocityField: "dangerFastVelocityLimit",
        timeField: "dangerFastTime"
      },
      {
        velocityField: "fastArrivalLimit",
        timeField: "fasttime"
      },
      {
        velocityField: "slowArrivalLimit",
        timeField: "slowtime"
      }
    ];

    for (const { velocityField, timeField } of bidirectionalCalcMap) {
      if (fieldName === velocityField) {
        let timeValue = "";
        if (tubingLength !== undefined && tubingLength !== null) {
          const tubingLengthNum = Number(tubingLength);
          const velocity = Number(newValue);
          if (!isNaN(tubingLengthNum) && !isNaN(velocity)) {
            if (velocity === 0) {
              timeValue = "0";
            } else {
              const result = tubingLengthNum / velocity;
              timeValue = isFinite(result) ? result.toFixed(2) : "0";
            }
          }
        } else {
          timeValue = "0";
        }
        setFormState((prevFormState) => {
          const updated = {
            ...prevFormState,
            [formName]: {
              ...prevFormState[formName],
              [timeField]: timeValue,
            },
          };

          if (rules) {
            const errs = validateDynamic(updated[formName], rules, filteredAfterflowInputs);
            setErrors(errs);
          }
          return updated;

        });
        break;
      }
      if (fieldName === timeField) {
        let velocityValue = "";
        if (tubingLength !== undefined && tubingLength !== null) {
          const tubingLengthNum = Number(tubingLength);
          let timeNum = Number(newValue);
          if (!isNaN(tubingLengthNum) && !isNaN(timeNum)) {
            if (timeNum === 0) {
              velocityValue = "0";
            } else {
              const result = tubingLengthNum / timeNum;
              velocityValue = isFinite(result) ? result.toFixed(2) : "0";
            }
          }
        } else {
          velocityValue = "0";
        }
        setFormState((prevFormState) => {
          const updated = {
            ...prevFormState,
            [formName]: {
              ...prevFormState[formName],
              [velocityField]: velocityValue,
            },
          };

          if (rules) {
            const errs = validateDynamic(updated[formName], rules, filteredAfterflowInputs);
            setErrors(errs);
          }
          return updated;
        });
        break;
      }
    }
  };

  {/* Utility for rendering input fields inside group containers */ }
  const renderGroupInputs = (groupData, groupName: string) => {
    const sortedGroupData = [...groupData].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );

    const normalizeLabel = (str: string) =>
      (str || "").toLowerCase().replace(/[^a-z]/g, "");

    const holdOpenIdx = sortedGroupData.findIndex(
      (item) => normalizeLabel(item.label) === "consecutivenonarrivalholdopencount"
    );
    const shutInIdx = sortedGroupData.findIndex(
      (item) => normalizeLabel(item.label) === "consecutivenonarrivalshutin"
    );

    if (holdOpenIdx !== -1 && shutInIdx !== -1 && holdOpenIdx !== shutInIdx + 1) {
      const [holdOpenItem] = sortedGroupData.splice(holdOpenIdx, 1);
      const newShutInIdx = sortedGroupData.findIndex(
        (item) => normalizeLabel(item.label) === "consecutivenonarrivalshutin"
      );
      sortedGroupData.splice(newShutInIdx + 1, 0, holdOpenItem);
    }

    // NEW: Disable groups when controlSetupValue === "5"
    const disabledGroupsForValue5 = [
      "lifting",
      "close auto adjustments",
      "adjustment windows",
      "afterflow auto adjustments"
    ];

    const isControlFive = controlSetupValue === "5";

    const isDisabledByControlSetup =
      isControlFive &&
      disabledGroupsForValue5.includes(groupName?.toLowerCase());

    // Check if any toggle in this group is OFF
    let disableGroupInputs = sortedGroupData.some((item) => {
      const value = formState[FormName]?.[item.name];
      return (
        toggleOnlyIds.includes(item.id) &&
        (value === "0" || value == null || value === "")
      );
    });

    const openTriggerValue = getOpenTriggerLabelFromClosedInputs(formState);
    const closedTriggerValue = getClosedTriggerLabelFromClosedInputs(formState);

    const isCloseAutoAdjustmentGroup =
      groupName?.toLowerCase() === "close auto adjustments";

    const isAfterflowAutoAdjustmentGroup =
      groupName?.toLowerCase() === "afterflow auto adjustments";

    const shouldGrayOutEntireGroup =
      (isCloseAutoAdjustmentGroup && openTriggerValue === "None") ||
      (isAfterflowAutoAdjustmentGroup && closedTriggerValue === "None");

    // Adjustment Windows toggle OFF
    const isAdjustmentWindowsDisabled = (() => {
      const toggleValue = formState[FormName]?.["AdjustmentWindowToggle"];
      return toggleValue?.toString() === "0";
    })();

    // Adjustment Windows edit tracking
    let editingFast = false;
    let editingFastTime = false;
    let editingSlow = false;
    let editingSlowTime = false;

    if (groupName === "Adjustment Windows") {
      editingFast = !!changedFields[FormName]?.has("fastArrivalLimit");
      editingFastTime = !!changedFields[FormName]?.has("fasttime");
      editingSlow = !!changedFields[FormName]?.has("slowArrivalLimit");
      editingSlowTime = !!changedFields[FormName]?.has("slowtime");
    }

    return sortedGroupData?.map(
      ({
        id,
        name,
        label,
        value,
        type,
        options,
        enable,
        unit,
        isGrayedOut,
        description,
      }) => {

        const hasError = !!errors[name];

        const isLiftingGroup = groupName?.toLowerCase() === "lifting";

        // Skip toggle inputs
        if (toggleOnlyIds.includes(id)) return null;

        if (isLiftingGroup && id === 252 && !isWellMethodConventional) {
          return (
            <div
              style={{
                visibility: "hidden",
                height: "60px",
                marginBottom: "12px",
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          );
        }

        if (isLiftingGroup && ishighLinePresseureMode && id === 240) {
          return null;
        }

        if (
          normalizeLabel(label) === "consecutivenonarrivalholdopencount" &&
          controlSetupValue !== "4"
        ) {
          return null;
        }

        // Consecutive Non-Arrival Hold Open Count is disabled (per spec) when its
        // value is 0 or blank — gray it out the same way the value drives it.
        const currentFieldValue = formState[FormName]?.[name] ?? value;
        const isHoldOpenCountZero =
          normalizeLabel(label) === "consecutivenonarrivalholdopencount" &&
          (currentFieldValue === undefined ||
            currentFieldValue === null ||
            currentFieldValue === "" ||
            Number(currentFieldValue) === 0);

        const shouldGrayOut =
          isGrayedOut ||
          shouldGrayOutEntireGroup ||
          isFieldGrayedOut(name) ||
          isHoldOpenCountZero;

        // FINAL DISABLE LOGIC (merged cleanly)
        let disabled =
          disableGroupInputs || isDisabledByControlSetup;

        const adjustmentWindowFields = new Set([
          "dangerFastVelocityLimit",
          "dangerFastTime",
          "fastArrivalLimit",
          "fasttime",
          "slowArrivalLimit",
          "slowtime",
        ]);

        if (isjwtIsPlLimitedUser && adjustmentWindowFields.has(name)) {
          disabled = true;
        }

        if (
          groupName?.toLowerCase() === "lifting" &&
          id === 240
        ) {
          disabled = disableGroupInputs; // ignore control setup disable
        }

        // Adjustment Windows logic
        if (groupName === "Adjustment Windows") {
          if (
            (editingFast && name.toLowerCase() === "fasttime") ||
            (editingFastTime &&
              name.toLowerCase() === "fastarrivallimit")
          )
            disabled = true;

          if (
            (editingSlow && name.toLowerCase() === "slowtime") ||
            (editingSlowTime &&
              name.toLowerCase() === "slowarrivallimit")
          )
            disabled = true;
        }

        // Adjustment Windows master disable behavior
        const adjustmentWindowDependentFields = [
          "fastOnTriggerAdjust",
          "slowOnTriggerAdjust",
          "fastArrivalLimit",
          "fasttime",
          "slowArrivalLimit",
          "slowtime",
          "fastAdjust",
          "slowAdjust",
          "goodAdjust",
          "fastOffTriggerAdjust",
          "slowOffTriggerAdjust",
        ];

        if (
          isAdjustmentWindowsDisabled &&
          adjustmentWindowDependentFields.includes(name)
        ) {
          disabled = true;
        }

        // Skip tubingLength
        if (name === "tubingLength") return null;

        let modifiedLabel = label;

        const labelsWithoutAdjustment = [
          "Open Trigger Max:",
          "Open Trigger Min:",
          "Close Trigger Max:",
          "Close Trigger Min:",
        ];

        const isExcluded = labelsWithoutAdjustment.includes(
          label.trim()
        );

        if (
          isCloseAutoAdjustmentGroup &&
          name.toLowerCase() !== "opentrigger" &&
          openTriggerValue !== "None"
        ) {
          modifiedLabel = `${label} ${openTriggerValue}${isExcluded ? "" : " Adjustment"
            }`;
        } else if (
          isAfterflowAutoAdjustmentGroup &&
          name.toLowerCase() !== "closetrigger" &&
          closedTriggerValue !== "None"
        ) {
          modifiedLabel = `${label} ${closedTriggerValue}${isExcluded ? "" : " Adjustment"
            }`;
        }

        return (
          <React.Fragment key={id}>
            {enable ? (
              <div
                className="tooltip-wrapper"
                style={{ display: "block", marginBottom: "6px" }}
              >
                <label>{modifiedLabel}</label>
                {isAfterflowAutoAdjustmentGroup && label?.trim() === "Close Trigger Max:" && closedTriggerValue === "Critical Rate Multiplier" && (
                  <span className="tooltip-text">Value must be ≤ 150%</span>
                )}
                {isAfterflowAutoAdjustmentGroup && label?.trim() === "Close Trigger Min:" && closedTriggerValue === "Critical Rate Multiplier" && (
                  <span className="tooltip-text">Value must be ≥ 80%</span>
                )}
                {description && (
                  <span className="tooltip-text">{description}</span>
                )}

                {type !== "dropdown" ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div className="relative w-full">
                      <div
                        className="absolute top-0 left-0 w-full h-full pointer-events-none flex items-center justify-end pr-2"
                        style={{
                          color:
                            shouldGrayOut || disabled
                              ? "#4A5463"
                              : "#0094BD",
                          fontSize: "18px",
                          padding: "6px",
                          borderRadius: "8px",
                          textAlign: "right",
                        }}
                      >
                        {unit ? `| ${unit}` : ""}
                      </div>

                      <input
                        type={
                          name.toLowerCase() === "ccid"
                            ? "string"
                            : "number"
                        }
                        value={formState[FormName]?.[name] ?? value}
                        onChange={(e) => {
                          const newValue = e.target.value;

                          const updatedForm = {
                            ...formState[FormName],
                            [name]: newValue,
                          };

                          handleInputChange(FormName, name, newValue);
                          handleFieldChange(FormName, name, newValue);

                          if (rules) {
                            const errs = validateDynamic(updatedForm, rules, filteredAfterflowInputs);
                            setErrors(errs);
                          }
                        }}
                        placeholder={label}
                        disabled={disabled}
                        style={{
                          height: "36px",
                          padding: "6px",
                          paddingRight: unit ? "5rem" : "6px",
                          color: "white",
                          borderRadius: "8px",
                          textAlign: "right",

                          backgroundColor:
                            hasError
                              ? "rgb(181 23 23)"
                              : changedFields[FormName]?.has(name)
                                ? "rgb(208 196 64)"
                                : shouldGrayOut || disabled
                                  ? "#131F2E"
                                  : "rgb(5 17 35)",

                          width: "100%",

                          outline:
                            hasError
                              ? "2px solid rgb(255, 255, 255)"
                              : shouldGrayOut || disabled
                                ? "1px solid #4A5463"
                                : "1px solid #0094BD",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        minHeight: "16px",
                        fontSize: "11px",
                        color: "#F97066",
                        marginTop: "2px",
                        lineHeight: "14px",
                      }}
                    >
                      {errors[name] ?? "\u00A0"}
                    </div>
                  </div>
                ) : (
                  <select
                    value={formState[FormName]?.[name] ?? value}
                    onChange={(e) => {
                      const newValue = e.target.value;

                      const updatedForm = {
                        ...formState[FormName],
                        [name]: newValue,
                      };

                      handleInputChange(FormName, name, newValue);
                      handleFieldChange(FormName, name, newValue);

                      if (rules) {
                        const errs = validateDynamic(updatedForm, rules, filteredAfterflowInputs);
                        setErrors(errs);
                      }
                    }}
                    disabled={disabled}
                    style={{
                      padding: "6px",
                      color: "white",
                      borderRadius: "8px",
                      textAlign: "left",

                      backgroundColor:
                        hasError
                          ? "rgb(181 23 23)"
                          : changedFields[FormName]?.has(name)
                            ? "rgb(208 196 64)"
                            : shouldGrayOut || disabled
                              ? "#131F2E"
                              : "rgb(5 17 35)",

                      width: "100%",

                      outline:
                        hasError
                          ? "2px solid rgb(255, 255, 255)"
                          : shouldGrayOut || disabled
                            ? "1px solid #4A5463"
                            : "1px solid #0094BD",
                    }}
                  >
                    {options?.map((option: any) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.text}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <div
                style={{
                  visibility: "hidden",
                  height: "60px",
                  marginBottom: "12px",
                }}
              />
            )}
            {name === "openTrigger" &&
              groupName.toLowerCase() === "closed" &&
              openTriggerValue === "None" && (
                <div style={{ visibility: 'hidden', height: '60px', marginBottom: '12px' }} />
              )}
            {name === "closeTrigger" &&
              groupName.toLowerCase() === "afterflow" &&
              closedTriggerValue === "None" && (
                <div style={{ visibility: 'hidden', height: '60px', marginBottom: '12px' }} />
              )}
          </React.Fragment>
        );
      }
    );
  };

  if (isSetPointLoading) {
    return <LoaderOverlay />
  }
  return (

    <div style={{ paddingLeft: "5px", paddingRight: "5px" }}>
      {/* Control Group - Top */}
      <div
        style={{
          marginBottom: "20px",
          border: "1px solid #FCFCFD",
          borderRadius: "8px",
          padding: "10px",
          minWidth: "250px",
          maxWidth: "100%",
        }}
      >
        <p style={{ color: "#FCFCFD", marginBottom: "10px", fontSize: "12px" }}>
          <span
            style={{
              border: "1px solid #FCFCFD",
              padding: "4px 4px",
              borderRadius: "4px",
            }}
          >
            CONTROL SETUP
          </span>
        </p>

        <hr
          style={{
            borderColor: "#FCFCFD",
            borderWidth: "1px",
            marginBottom: "4px",
          }}
        />



        {renderGroupInputs(
          PlungerFormData?.filter(
            (item) => item.subGroup === "Control Setup"
          ), "Control Setup"
        )}
      </div>


      {(() => {
        const allGroups = PlungerFormData?.filter(
          (item) => item.subGroup !== "Control Setup" && item.subGroup != null
        );

        // Grouping by subGroup
        const groupedByGroup: { [subGroup: string]: typeof allGroups } = {};
        allGroups.forEach((item) => {
          if (!groupedByGroup[item.subGroup]) {
            groupedByGroup[item.subGroup] = [];
          }
          groupedByGroup[item.subGroup].push(item);
        });

        // The 4 special groups
        const injectionGroups = [
          "Close Injection",
          "Gas Injection",
          "AfterFlow Injection",
          "PAGL Adjustment"
        ];

        // Full fixed order
        const fixedGroupOrder = [
          "Closed",
          "Lifting",
          "Afterflow",
          "Close Injection",
          "Gas Injection",
          "AfterFlow Injection",
          "Close Auto Adjustments",
          "Adjustment Windows",
          "PAGL Adjustment",
          "Afterflow Auto Adjustments"
        ];

        // Decide which groups to render

        const groupsToRender =
          controlSetupValue === "4"
            ? fixedGroupOrder // show all groups including injection + PAGL
            : fixedGroupOrder.filter((g) => !injectionGroups.includes(g)); // exclude them

        const baseColors = ["#F97066", "#4294FF", "#32D583"];
        const extraColorsForMode4 = ["#FFD700", "#FFD700", "#FFD700"];

        const colors = controlSetupValue === "4"
          ? [...baseColors, ...extraColorsForMode4, ...baseColors] // maintains order
          : [...baseColors, ...baseColors]; // repeats baseColors to match original length

        // Check if both special groups exist
        const hasBothSpecialGroups =
          groupsToRender.includes("Adjustment Windows") &&
          groupsToRender.includes("PAGL Adjustment");

        // Prepare groups list to skip PAGL Adjustment later if Adjustment Windows is also present
        const finalGroupsToRender = hasBothSpecialGroups
          ? groupsToRender.filter((g) => g !== "PAGL Adjustment")
          : groupsToRender;


        const numRows = Math.ceil(finalGroupsToRender.length / 3); // calculate required rows dynamically

        return Array.from({ length: numRows }, (_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            {finalGroupsToRender
              .slice(rowIndex * 3, rowIndex * 3 + 3) // take 3 containers per row
              .map((groupName, colIndex) => {

                const groupData = groupedByGroup[groupName] || [];
                const color = colors[rowIndex * 3 + colIndex];
                // hide conditionally
                const shouldHide =
                  !isLatestVersion &&
                  (groupName === "Close Injection" || groupName === "AfterFlow Injection");

                const isAdjustmentWindows = groupName === "Adjustment Windows";
                const getFlexValue = () => {
                  if (!isAdjustmentWindows) return undefined;
                  if (typeof window !== "undefined" && window.innerWidth < 1024) {
                    return "1 1 calc(100% - 5px)";
                  }
                  return "1 1 calc(50% - 5px)";
                };
                const disabledGroupsForValue5 = [
                  "lifting",
                  "close auto adjustments",
                  "adjustment windows",
                  "afterflow auto adjustments"
                ];

                const isDisabledGroup =
                  controlSetupValue === "5" &&
                  disabledGroupsForValue5.includes(groupName?.toLowerCase());

                return (
                  <div
                    key={groupName}
                    style={{
                      flex: "1 1 100%",
                      minWidth: "250px",
                      maxWidth: "100%",
                      border: isAdjustmentWindows && hasBothSpecialGroups ? "" : `2px solid ${color}`,
                      borderRadius: "8px",
                      padding: isAdjustmentWindows && hasBothSpecialGroups ? "" : "10px 10px",
                      boxSizing: "border-box",
                      ...(typeof window !== "undefined" &&
                        window.innerWidth >= 768 && {
                        flex: "1 1 calc(33.333% - 10px)",
                        visibility: shouldHide ? "hidden" : "visible",
                      }),
                    }}
                  >
                    {isAdjustmentWindows && hasBothSpecialGroups ? null : (
                      <><p
                        style={{
                          color: color,
                          marginBottom: "10px",
                          fontSize: "12px",
                          display: "flex",
                          justifyContent: "space-between", // pushes toggle to right
                          alignItems: "center",            // vertically centers toggle with text
                        }}
                      >
                        {/* Left side: group name */}
                        <span
                          style={{
                            border: `1px solid ${color}`,
                            padding: "2px 2px",
                            borderRadius: "4px",
                          }}
                        >
                          {groupName.toLocaleUpperCase()}
                        </span>

                        {isLatestVersion && groupData
                          .filter((item) => toggleOnlyIds.includes(item.id)) // only the toggle IDs
                          .map((item) => (                                 // define item here
                            <label key={item.id} className="switch" style={{ marginLeft: "8px" }}>
                              <input
                                type="checkbox"
                                checked={formState[FormName]?.[item.name] === "1"} // read value from formState
                                onChange={(e) =>
                                  handleInputChange(
                                    FormName,
                                    item.name,                // update by field name
                                    e.target.checked ? "1" : "0"
                                  )
                                }
                                disabled={isDisabledGroup}
                                style={{
                                  opacity: isDisabledGroup ? 0.6 : 1,
                                  cursor: isDisabledGroup ? "not-allowed" : "default"
                                }}
                              />
                              <span className="slider round"></span>
                            </label>
                          ))}

                      </p>
                        <hr
                          style={{
                            borderColor: color,
                            borderWidth: "1px",
                            marginBottom: "4px",
                          }}
                        />

                      </>
                    )}

                    {/* Conditional layout for Adjustment Windows */}
                    {isAdjustmentWindows && hasBothSpecialGroups ? (
                      // Render both vertically
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {/* Adjustment Windows Container */}
                        <div
                          style={{
                            minWidth: "250px",
                            border: `2px solid ${colors[groupsToRender.indexOf("Adjustment Windows")]}`,
                            borderRadius: "8px",
                            padding: "10px",
                            boxSizing: "border-box",
                          }}
                        >
                          <p style={{
                            color: colors[groupsToRender.indexOf("Adjustment Windows")], marginBottom: "10px", fontSize: "12px", display: "flex",
                            justifyContent: "space-between", // pushes toggle to right
                            alignItems: "center",
                          }}>
                            <span
                              style={{
                                border: `1px solid ${colors[groupsToRender.indexOf("Adjustment Windows")]}`,
                                padding: "4px 4px",
                                borderRadius: "4px",
                              }}
                            >
                              ADJUSTMENT WINDOWS
                            </span>
                            {/* Right side: toggle */}
                            {isLatestVersion &&
                              <label className="switch" style={{ marginLeft: "8px" }}>
                                <input
                                  type="checkbox"
                                  checked={formState[FormName]?.AdjustmentWindowToggle === "1"}
                                  onChange={(e) =>
                                    handleInputChange(FormName, "AdjustmentWindowToggle", e.target.checked ? "1" : "0")
                                  }
                                  disabled={isDisabledGroup}
                                  style={{
                                    opacity: isDisabledGroup ? 0.6 : 1,
                                    cursor: isDisabledGroup ? "not-allowed" : "default"
                                  }}
                                />
                                <span className="slider round"></span>
                              </label>
                            }
                          </p>
                          <hr
                            style={{
                              borderColor: colors[groupsToRender.indexOf("Adjustment Windows")],
                              borderWidth: "1px",
                              marginBottom: "4px",
                            }}
                          />
                          <div style={{ display: "flex", flexWrap: "wrap", columnGap: "5px", rowGap: 0 }}>
                            {(() => {
                              const inputs = groupedByGroup["Adjustment Windows"] || [];
                              // Render the entire group at once
                              const renderedInputs = renderGroupInputs(inputs, "Adjustment Windows");

                              return renderedInputs
                                .filter(Boolean) // removes null / undefined
                                .map((inputElement, idx) => (
                                  <div key={idx} style={{ flex: getFlexValue() }}>
                                    {inputElement}
                                  </div>
                                ));

                            })()}
                          </div>

                        </div>

                        {/* PAGL Adjustment Container */}
                        <div
                          style={{
                            minWidth: "250px",
                            border: `2px solid #FFD700`,
                            borderRadius: "8px",
                            padding: "10px",
                            boxSizing: "border-box",
                          }}
                        >
                          <p style={{
                            color: "#FFD700", marginBottom: "10px", fontSize: "12px", display: "flex",
                            justifyContent: "space-between", // pushes toggle to right
                            alignItems: "center",
                          }}>
                            <span
                              style={{
                                border: `1px solid #FFD700`,
                                padding: "4px 4px",
                                borderRadius: "4px",

                              }}
                            >
                              PAGL ADJUSTMENT
                            </span>
                            {isLatestVersion &&
                              <label className="switch" style={{ marginLeft: "8px" }}>
                                <input
                                  type="checkbox"
                                  checked={formState[FormName]?.PAGLAdjustToggle === "1"}
                                  onChange={(e) =>
                                    handleInputChange(FormName, "PAGLAdjustToggle", e.target.checked ? "1" : "0")
                                  }
                                />
                                <span className="slider round"></span>
                              </label>
                            }

                          </p>
                          <hr
                            style={{
                              borderColor: "#FFD700",
                              borderWidth: "1px",
                              marginBottom: "4px",
                            }}
                          />
                          <div style={{ display: "flex", flexWrap: "wrap", columnGap: "5px", rowGap: 0 }}>
                            {(() => {
                              const inputs = groupedByGroup["PAGL Adjustment"] || [];
                              // Render the entire group at once
                              const renderedInputs = renderGroupInputs(inputs, "PAGL Adjustment");

                              return renderedInputs
                                .filter(Boolean) // removes null / undefined
                                .map((inputElement, idx) => (
                                  <div key={idx} style={{ flex: getFlexValue() }}>
                                    {inputElement}
                                  </div>
                                ));

                            })()}
                          </div>

                        </div>
                      </div>
                    ) : isAdjustmentWindows ? (
                      // Normal Adjustment Windows rendering if PAGL Adjustment not available
                      <div style={{ display: "flex", flexWrap: "wrap", columnGap: "5px", rowGap: 0 }}>
                        {(() => {
                          const inputs = groupedByGroup["Adjustment Windows"] || [];
                          // Render the entire group at once
                          const renderedInputs = renderGroupInputs(inputs, "Adjustment Windows");

                          return renderedInputs
                            .filter(Boolean) // removes null / undefined
                            .map((inputElement, idx) => (
                              <div key={idx} style={{ flex: getFlexValue() }}>
                                {inputElement}
                              </div>
                            ));
                        })()}
                      </div>
                    ) : groupName === "Closed" ? (
                      renderGroupInputs(filteredClosedInputs, groupName)
                    ) : groupName === "Afterflow" ? (
                      renderGroupInputs(filteredAfterflowInputs, groupName)
                    ) : (
                      renderGroupInputs(groupData, groupName)
                    )}
                  </div>
                );
              })}
          </div>
        ));

      })()}



    </div>
  );

};

export default PlungerForm;
